import { tr, usePartnerTranslation } from "../../lib/language";
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { formatPartnerDate as format } from "../../lib/language";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Inbox,
  Loader2,
  X,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { parseApiError } from "../../lib/api";
import { formatMoney, normalizeCurrency } from "../../lib/money";
import { cn } from "../../lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PER_PAGE = 15;

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return format(new Date(`${iso}T00:00:00`), "d MMM yyyy");
  } catch {
    return iso;
  }
};

const formatPeriod = (start, end) => {
  if (!start || !end) return "—";
  try {
    const s = format(new Date(`${start}T00:00:00`), "d MMM");
    const e = format(new Date(`${end}T00:00:00`), "d MMM yyyy");
    return `${s} – ${e}`;
  } catch {
    return `${start} – ${end}`;
  }
};

/* ---------- Status badge ---------- */
const STATUS_STYLES = {
  draft:      "bg-[#FFF3CC] text-[#7A5000]",
  submitted:  "bg-[#D4E8FF] text-[#0C3F7A]",
  approved:   "bg-[#D4E8FF] text-[#0C3F7A]",
  executed:   "bg-[#D4F7DF] text-[#0F6B2B]",
  reconciled: "bg-[#D4F7DF] text-[#0F6B2B]",
  onhold:     "bg-[#FFF3CC] text-[#7A5000]",
  cancelled:  "bg-[#FFD4D4] text-[#9B0000]",
};

const StatusBadge = ({
  status,
  label
}) => {
  usePartnerTranslation();
  return <span className={cn("inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold", STATUS_STYLES[status] || "bg-[#F3EEEE] text-[#6B7280]")}>
    {label}
  </span>;
};

/* ---------- Status filter ---------- */
const STATUS_OPTIONS = [
  { value: "", get label() { return tr("All statuses"); } },
  { value: "draft", get label() { return tr("Draft"); } },
  { value: "submitted", get label() { return tr("Submitted"); } },
  { value: "approved", get label() { return tr("Approved"); } },
  { value: "executed", get label() { return tr("Executed"); } },
  { value: "reconciled", get label() { return tr("Reconciled"); } },
  { value: "onhold", get label() { return tr("On Hold"); } },
  { value: "cancelled", get label() { return tr("Cancelled"); } },
];

const StatusFilter = ({ value, onChange }) => {
  usePartnerTranslation();
  const [open, setOpen] = useState(false);
  const current = STATUS_OPTIONS.find((o) => o.value === value) || STATUS_OPTIONS[0];
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="payouts-status-filter"
          className="inline-flex h-10 min-w-[150px] items-center justify-between gap-2 rounded-lg border border-[#D7DAE0] bg-white px-3 text-sm font-medium text-[#0F1115] transition-colors hover:border-[#0F1115]"
        >
          <span>{current.label}</span>
          <ChevronDown className="h-4 w-4 text-[#6B7280]" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-44 rounded-xl border border-[#EAECEF] p-1 shadow-lg"
      >
        {STATUS_OPTIONS.map((o) => (
          <button
            key={o.value}
            type="button"
            data-testid={`status-option-${o.value || "all"}`}
            onClick={() => { onChange(o.value); setOpen(false); }}
            className={cn(
              "flex w-full items-center justify-between rounded-md px-3 py-2 text-sm font-medium transition-colors",
              o.value === value
                ? "bg-[#FFF2F2] text-[#FF5757]"
                : "text-[#0F1115] hover:bg-[#F7F7F9]",
            )}
          >
            <span>{o.label}</span>
            {o.value === value && (
              <span className="inline-block h-2 w-2 rounded-full bg-[#FF5757]" />
            )}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
};

/* ---------- Pagination ---------- */
const Pager = ({ page, totalPages, onChange }) => {
  usePartnerTranslation();
  const pages = useMemo(() => {
    if (totalPages <= 1) return [];
    const max = 5;
    if (totalPages <= max) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const start = Math.min(Math.max(1, page - Math.floor(max / 2)), totalPages - max + 1);
    return Array.from({ length: max }, (_, i) => start + i);
  }, [page, totalPages]);

  if (totalPages <= 1) return null;

  const btn = "inline-flex h-9 w-9 items-center justify-center rounded-md text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40";

  return (
    <nav aria-label={tr("Pagination")} className="mt-8 flex items-center justify-center gap-1 text-[#0F1115]">
      <button type="button" onClick={() => onChange(1)} disabled={page === 1} aria-label={tr("First page")} className={cn(btn, "hover:bg-[#F3EEEE]")}>
        <ChevronsLeft className="h-4 w-4" />
      </button>
      <button type="button" onClick={() => onChange(Math.max(1, page - 1))} disabled={page === 1} aria-label={tr("Previous page")} className={cn(btn, "hover:bg-[#F3EEEE]")}>
        <ChevronLeft className="h-4 w-4" />
      </button>
      {pages[0] > 1 && <span className="px-1 text-sm text-[#9BA1AB]">…</span>}
      {pages.map((p) => {
        const active = p === page;
        return (
          <button key={p} type="button" onClick={() => onChange(p)} aria-current={active ? "page" : undefined}
            className={cn(btn, active ? "text-[#FF5757]" : "text-[#0F1115] hover:bg-[#F3EEEE]")}>
            <span className={active ? "font-extrabold underline underline-offset-4 decoration-2" : ""}>{p}</span>
          </button>
        );
      })}
      {pages[pages.length - 1] < totalPages && <span className="px-1 text-sm text-[#9BA1AB]">…</span>}
      <button type="button" onClick={() => onChange(Math.min(totalPages, page + 1))} disabled={page === totalPages} aria-label={tr("Next page")} className={cn(btn, "hover:bg-[#F3EEEE]")}>
        <ChevronRight className="h-4 w-4" />
      </button>
      <button type="button" onClick={() => onChange(totalPages)} disabled={page === totalPages} aria-label={tr("Last page")} className={cn(btn, "hover:bg-[#F3EEEE]")}>
        <ChevronsRight className="h-4 w-4" />
      </button>
    </nav>
  );
};

/* ---------- Payout detail sheet ---------- */
const PayoutDetailSheet = ({ payout, open, onClose }) => {
  usePartnerTranslation();
  if (!payout) return null;
  const cur = normalizeCurrency(payout.currency);

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full max-w-xl overflow-y-auto p-0 sm:max-w-xl">
        <SheetHeader className="border-b border-[#EAECEF] px-6 py-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <SheetTitle className="font-mono text-lg font-extrabold text-[#0F1115]">
                {payout.reference}
              </SheetTitle>
              <p className="mt-0.5 text-sm text-[#6B7280]">
                {formatPeriod(payout.period_start, payout.period_end)}
              </p>
            </div>
            <StatusBadge status={payout.status} label={payout.status_label} />
          </div>
        </SheetHeader>

        {/* Summary amounts */}
        <div className="grid grid-cols-3 gap-px bg-[#EAECEF] border-b border-[#EAECEF]">
          {[
            { label: tr("Gross"), value: formatMoney(payout.gross_amount, cur) },
            { label: tr("Commission"), value: formatMoney(payout.commission_amount, cur) },
            { label: tr("Net"), value: formatMoney(payout.net_amount, cur) },
          ].map((s) => (
            <div key={s.label} className="bg-[#FAFAFB] px-5 py-4">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]">{s.label}</p>
              <p className="mt-1 text-base font-extrabold text-[#0F1115]">{s.value}</p>
            </div>
          ))}
        </div>

        {payout.paid_at && (
          <div className="border-b border-[#EAECEF] px-6 py-3">
            <p className="text-xs text-[#6B7280]">{tr("Paid on ")}<span className="font-semibold text-[#0F1115]">{format(new Date(payout.paid_at), "d MMM yyyy")}</span>
            </p>
          </div>
        )}

        {/* Booking items */}
        <div className="px-6 py-5">
          <p className="text-sm font-semibold text-[#0F1115]">{tr("Bookings (")}{payout.booking_count})
          </p>
          {payout.items && payout.items.length > 0 ? (
            <div className="mt-3 space-y-2">
              {payout.items.map((item) => (
                <div
                  key={item.booking_reference}
                  className="rounded-xl border border-[#EAECEF] bg-white p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-mono text-sm font-semibold text-[#0F1115]">
                        {item.booking_reference}
                      </p>
                      <p className="mt-0.5 text-xs text-[#6B7280] line-clamp-1">
                        {item.product_title}
                      </p>
                    </div>
                    <p className="text-sm font-bold text-[#0F1115] whitespace-nowrap">
                      {formatMoney(item.net_amount, cur)}
                    </p>
                  </div>
                  <div className="mt-2 flex items-center gap-4 text-xs text-[#9BA1AB]">
                    <span>{formatDate(item.activity_date)}</span>
                    <span>{tr("Gross ")}{formatMoney(item.gross_amount, cur)}</span>
                    <span>{tr("Comm ")}{formatMoney(item.commission_amount, cur)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-3 text-sm text-[#9BA1AB]">{tr("No booking items.")}</p>
          )}
        </div>

        {payout.notes && (
          <div className="border-t border-[#EAECEF] px-6 py-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-[#6B7280]">{tr("Notes")}</p>
            <p className="mt-1 text-sm text-[#0F1115]">{payout.notes}</p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

/* ---------- Desktop table row ---------- */
const PayoutRow = ({
  payout,
  onSelect
}) => {
  usePartnerTranslation();
  return <tr data-testid={`payout-row-${payout.reference}`} onClick={() => onSelect(payout)} className="cursor-pointer border-b border-[#F0EFEF] transition-colors hover:bg-[#FFF7F7]">
    <td className="whitespace-nowrap px-4 py-4 font-mono text-sm font-semibold text-[#0F1115]">
      {payout.reference}
    </td>
    <td className="whitespace-nowrap px-4 py-4 text-sm text-[#0F1115]">
      {formatPeriod(payout.period_start, payout.period_end)}
    </td>
    <td className="px-4 py-4">
      <StatusBadge status={payout.status} label={payout.status_label} />
    </td>
    <td className="whitespace-nowrap px-4 py-4 text-end text-sm text-[#0F1115]">
      {payout.booking_count}
    </td>
    <td className="whitespace-nowrap px-4 py-4 text-end text-sm text-[#0F1115]">
      {formatMoney(payout.gross_amount, payout.currency)}
    </td>
    <td className="whitespace-nowrap px-4 py-4 text-end text-sm text-[#6B7280]">
      −{formatMoney(payout.commission_amount, payout.currency)}
    </td>
    <td className="whitespace-nowrap px-4 py-4 text-end text-sm font-semibold text-[#0F1115]">
      {formatMoney(payout.net_amount, payout.currency)}
    </td>
    <td className="whitespace-nowrap px-4 py-4 text-end text-sm text-[#9BA1AB]">
      {payout.paid_at ? format(new Date(payout.paid_at), "d MMM yyyy") : "—"}
    </td>
  </tr>;
};

/* ---------- Mobile card ---------- */
const PayoutCard = ({
  payout,
  onSelect
}) => {
  usePartnerTranslation();
  return <article data-testid={`payout-card-${payout.reference}`} onClick={() => onSelect(payout)} className="cursor-pointer rounded-xl border border-[#EAECEF] bg-white p-4 transition-colors hover:border-[#FF5757]/30 hover:bg-[#FFF7F7]">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="font-mono text-sm font-bold text-[#0F1115]">{payout.reference}</p>
        <p className="mt-0.5 text-xs text-[#6B7280]">
          {formatPeriod(payout.period_start, payout.period_end)}
        </p>
      </div>
      <StatusBadge status={payout.status} label={payout.status_label} />
    </div>
    <div className="mt-3 grid grid-cols-3 gap-2 text-center text-sm">
      {[{
        label: tr("Gross"),
        value: formatMoney(payout.gross_amount, payout.currency)
      }, {
        label: tr("Comm"),
        value: formatMoney(payout.commission_amount, payout.currency)
      }, {
        label: tr("Net"),
        value: formatMoney(payout.net_amount, payout.currency)
      }].map(s => <div key={s.label} className="rounded-lg bg-[#FAFAFB] px-2 py-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-[#9BA1AB]">{s.label}</p>
          <p className="mt-0.5 text-xs font-bold text-[#0F1115]">{s.value}</p>
        </div>)}
    </div>
    {payout.paid_at && <p className="mt-2 text-end text-xs text-[#9BA1AB]">{tr("Paid ")}{format(new Date(payout.paid_at), "d MMM yyyy")}
      </p>}
  </article>;
};

/* ---------- Main tab ---------- */
const PaymentsTab = () => {
  usePartnerTranslation();
  const [items, setItems]         = useState([]);
  const [page, setPage]           = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected]   = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const handleSelect = (payout) => {
    setSelected(payout);
    setSheetOpen(true);
  };

  // Reset to page 1 when filter changes
  const handleStatusChange = (v) => {
    setStatusFilter(v);
    setPage(1);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const params = { page, per_page: PER_PAGE };
        if (statusFilter) params.status = statusFilter;
        const res = await axios.get(`${API}/finance/payouts`, { params });
        if (cancelled) return;
        setItems(res.data.items || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.total_pages || 0);
      } catch (err) {
        const { message } = parseApiError(err, tr("Couldn't load payouts."));
        if (!cancelled && process.env.NODE_ENV !== "production") {
          console.warn("[finance/payouts] load failed", message, err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [page, statusFilter]);

  const totalNet = useMemo(
    () => items.reduce((s, p) => s + (Number(p.net_amount) || 0), 0),
    [items],
  );

  return (
    <section data-testid="tab-payments-content" className="mt-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
            {statusFilter
              ? tr("{{value0}} payouts", { value0: STATUS_OPTIONS.find((option) => option.value === statusFilter)?.label })
              : tr("All payouts")}
          </p>
          <p className="mt-1 text-2xl font-extrabold tracking-tight text-[#0F1115]">
            {loading && items.length === 0
              ? "—"
              : tr("Payouts: {{count}}", { count: total })}
          </p>
        </div>
        <StatusFilter value={statusFilter} onChange={handleStatusChange} />
      </div>

      <div className="mt-6">
        {loading && items.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-[#FF5757]" />
          </div>
        ) : items.length === 0 ? (
          <div
            data-testid="payouts-empty"
            className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[#EAECEF] bg-[#FAFAFB] py-16 text-center"
          >
            <Inbox className="h-12 w-12 text-[#CFD2D8]" strokeWidth={1.3} />
            <p className="mt-4 text-lg font-semibold text-[#0F1115]">{tr("No payouts yet")}</p>
            <p className="mt-1 max-w-md text-sm text-[#6B7280]">{tr("Your payout history will appear here once Voocly processes your first payment cycle.")}</p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden overflow-x-auto rounded-xl border border-[#EAECEF] bg-white md:block">
              <table className="w-full min-w-[860px] border-collapse text-start">
                <thead>
                  <tr className="border-b border-[#EAECEF] bg-[#FAFAFB] text-[12px] font-semibold uppercase tracking-wider text-[#6B7280]">
                    <th className="px-4 py-3">{tr("Reference")}</th>
                    <th className="px-4 py-3">{tr("Period")}</th>
                    <th className="px-4 py-3">{tr("Status")}</th>
                    <th className="px-4 py-3 text-end">{tr("Bookings")}</th>
                    <th className="px-4 py-3 text-end">{tr("Gross")}</th>
                    <th className="px-4 py-3 text-end">{tr("Commission")}</th>
                    <th className="px-4 py-3 text-end">{tr("Net")}</th>
                    <th className="px-4 py-3 text-end">{tr("Paid date")}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => (
                    <PayoutRow key={p.id} payout={p} onSelect={handleSelect} />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="grid grid-cols-1 gap-3 md:hidden">
              {items.map((p) => (
                <PayoutCard key={p.id} payout={p} onSelect={handleSelect} />
              ))}
            </div>

            <Pager page={page} totalPages={totalPages} onChange={setPage} />
          </>
        )}
      </div>

      <PayoutDetailSheet
        payout={selected}
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
      />
    </section>
  );
};

export default PaymentsTab;
